/**
 * Track tab: the quick launcher. Shows how long it has been since the last
 * feeding, suggests the next breast side, and opens the logging sheets.
 * When a timer session is in progress (persisted by FeedSheet), the Feed
 * card becomes a live clock that reopens the session.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNav } from '../App';
import FeedSheet, {
  ACTIVE_FEED_KEY,
  sessionElapsedSeconds,
  type ActiveFeedSession,
} from '../components/FeedSheet';
import { DiaperSheet, PumpSheet, SleepSheet, WeightSheet } from '../components/TrackSheets';
import { ScreenHeader } from '../components/ui';
import { useT } from '../i18n/i18n';
import { kvGet } from '../lib/db';
import { useApp } from '../lib/store';
import { elapsedParts, formatDuration } from '../lib/time';
import type { FeedEvent } from '../lib/types';

// ── Local icons (stroke inherits currentColor, matching ui.tsx style) ──────

function Icon({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? 'h-6 w-6'}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const icons = {
  feed: (c?: string) => (
    <Icon className={c}>
      <path d="M12 3.5C9.2 7.7 6.5 10.9 6.5 14.5a5.5 5.5 0 0 0 11 0c0-3.6-2.7-6.8-5.5-11Z" />
    </Icon>
  ),
  diaper: (c?: string) => (
    <Icon className={c}>
      <path d="M4 7h16v4a9 9 0 0 1-6.8 8.7l-.2.3h-2l-.2-.3A9 9 0 0 1 4 11V7Z" />
      <path d="M4 11a5 5 0 0 1 5 5m11-5a5 5 0 0 0-5 5" />
    </Icon>
  ),
  pump: (c?: string) => (
    <Icon className={c}>
      <path d="M10 3h4M12 3v3" />
      <path d="M9.5 6h5l1 3v9.5a2.5 2.5 0 0 1-2.5 2.5h-2a2.5 2.5 0 0 1-2.5-2.5V9l1-3Z" />
      <path d="M8.5 12.5h7" />
    </Icon>
  ),
  sleep: (c?: string) => (
    <Icon className={c}>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    </Icon>
  ),
  weight: (c?: string) => (
    <Icon className={c}>
      <rect x="3.5" y="4" width="17" height="16" rx="3" />
      <path d="M8.2 10a4.8 4.8 0 0 1 7.6 0l-2.1 2.3a1.9 1.9 0 0 0-3.4 0Z" />
    </Icon>
  ),
};

type SheetKind = 'feed' | 'diaper' | 'pump' | 'sleep' | 'weight' | null;

export default function TrackScreen() {
  const t = useT();
  const nav = useNav();
  const { events } = useApp();

  const [sheet, setSheet] = useState<SheetKind>(null);
  const [activeFeed, setActiveFeed] = useState<ActiveFeedSession | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Reload the persisted timer session whenever no sheet is open, so the
  // Feed card reflects what happened inside the FeedSheet.
  useEffect(() => {
    if (sheet !== null) return;
    let cancelled = false;
    void kvGet<ActiveFeedSession>(ACTIVE_FEED_KEY).then((s) => {
      if (!cancelled) setActiveFeed(s ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [sheet]);

  // Tick every second while a feed is running, every 30s otherwise
  // (the "ago" label only changes minute to minute).
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), activeFeed ? 1000 : 30_000);
    return () => clearInterval(id);
  }, [activeFeed]);

  const lastFeed = events.find((e): e is FeedEvent => e.type === 'feed');
  const lastBreast = events.find(
    (e): e is FeedEvent => e.type === 'feed' && e.method === 'breast',
  );

  // Suggest the opposite of where the last breast feed started; fall back
  // to the opposite of the side that was used.
  let nextSide: 'left' | 'right' | null = null;
  if (lastBreast) {
    const from =
      lastBreast.startedSide ??
      (lastBreast.side === 'left' || lastBreast.side === 'right' ? lastBreast.side : undefined);
    if (from) nextSide = from === 'left' ? 'right' : 'left';
  }

  let agoLabel = '';
  if (lastFeed) {
    const { hours, minutes } = elapsedParts(lastFeed.endAt ?? lastFeed.at, now);
    agoLabel =
      hours === 0 && minutes === 0 ? t.common.time.justNow : t.common.time.agoHM(hours, minutes);
  }

  const quickCards: { key: Exclude<SheetKind, 'feed' | null>; label: string; icon: ReactNode }[] = [
    { key: 'diaper', label: t.track.cards.diaper, icon: icons.diaper('h-6 w-6') },
    { key: 'pump', label: t.track.cards.pump, icon: icons.pump('h-6 w-6') },
    { key: 'sleep', label: t.track.cards.sleep, icon: icons.sleep('h-6 w-6') },
    { key: 'weight', label: t.track.cards.weight, icon: icons.weight('h-6 w-6') },
  ];

  return (
    <>
      <ScreenHeader title={t.track.title} onSettings={() => nav.go('settings')} />

      <div className="space-y-4 px-4 py-4">
        <section className="card p-5">
          {lastFeed ? (
            <>
              <p className="text-sm text-ink-soft">{t.track.hero.sinceLastFeed}</p>
              <p className="mt-1 text-3xl font-bold text-ink">{agoLabel}</p>
              {nextSide && (
                <span className="mt-3 inline-flex items-center rounded-full bg-accent-soft px-3 py-1.5 text-sm font-bold text-accent">
                  {t.track.hero.nextSide(nextSide)}
                </span>
              )}
            </>
          ) : (
            <>
              <h2 className="text-lg">{t.track.hero.emptyTitle}</h2>
              <p className="mt-1 text-sm text-ink-soft">{t.track.hero.emptyBody}</p>
            </>
          )}
        </section>

        <button
          type="button"
          onClick={() => setSheet('feed')}
          className="w-full rounded-3xl bg-primary px-6 py-5 text-left text-on-primary shadow transition-transform active:scale-[0.98]"
        >
          {activeFeed ? (
            <span className="flex items-center justify-between gap-3">
              <span>
                <span className="flex items-center gap-2 text-sm font-bold">
                  <span className="h-2.5 w-2.5 animate-pulse-soft rounded-full bg-on-primary" />
                  {t.track.cards.feedActive}
                </span>
                <span className="mt-1 block text-3xl font-bold tabular-nums">
                  {formatDuration(sessionElapsedSeconds(activeFeed, now))}
                </span>
              </span>
              <span className="shrink-0 text-sm font-bold opacity-90">
                {t.track.cards.resume}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-3">
              {icons.feed('h-8 w-8')}
              <span className="text-xl font-bold">{t.track.cards.feed}</span>
            </span>
          )}
        </button>

        <div className="grid grid-cols-2 gap-3">
          {quickCards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => setSheet(card.key)}
              className="card flex min-h-27 flex-col items-center justify-center gap-2 p-4 transition-transform active:scale-[0.98]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-soft text-primary">
                {card.icon}
              </span>
              <span className="font-bold text-ink">{card.label}</span>
            </button>
          ))}
        </div>
      </div>

      <FeedSheet open={sheet === 'feed'} onClose={() => setSheet(null)} />
      <DiaperSheet open={sheet === 'diaper'} onClose={() => setSheet(null)} />
      <PumpSheet open={sheet === 'pump'} onClose={() => setSheet(null)} />
      <SleepSheet open={sheet === 'sleep'} onClose={() => setSheet(null)} />
      <WeightSheet open={sheet === 'weight'} onClose={() => setSheet(null)} />
    </>
  );
}
